import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import log from 'loglevel';

import type {
  DeletePushSubscriptionMutation,
  DeletePushSubscriptionMutationVariables,
} from './mutations/deletePushSubscription.mutation.graphql';
import DELETE_PUSH_SUBSCRIPTION from './mutations/deletePushSubscription.mutation.graphql';
import type {
  SavePushSubscriptionMutation,
  SavePushSubscriptionMutationVariables,
} from './mutations/savePushSubscription.mutation.graphql';
import SAVE_PUSH_SUBSCRIPTION from './mutations/savePushSubscription.mutation.graphql';
import type {
  PushPublicKeyQuery,
  PushPublicKeyQueryVariables,
} from './queries/pushPublicKey.query.graphql';
import PUSH_PUBLIC_KEY from './queries/pushPublicKey.query.graphql';

/**
 * `navigator.serviceWorker.ready` never resolves if no worker ever activates
 * (registration failure, scope mismatch, etc.). Race it against a timeout so a
 * stuck registration can't leave `busy` on forever with the toggle disabled.
 */
const SW_READY_TIMEOUT_MS = 10_000;

async function serviceWorkerReady(): Promise<ServiceWorkerRegistration> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('The service worker did not become ready in time.')),
      SW_READY_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Convert a base64url VAPID key into the Uint8Array PushManager expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export interface UsePushNotificationsResult {
  /** Browser supports the APIs we need (service worker + push + notifications). */
  isSupported: boolean;
  /** Server has VAPID keys configured (pushPublicKey is non-null). */
  isConfiguredOnServer: boolean;
  /** Current Notification permission ('default' | 'granted' | 'denied'). */
  permission: NotificationPermission;
  /** This device currently has an active push subscription. */
  isSubscribed: boolean;
  busy: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsResult {
  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState<NotificationPermission>(
    isSupported ? Notification.permission : 'denied',
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery<PushPublicKeyQuery, PushPublicKeyQueryVariables>(PUSH_PUBLIC_KEY, {
    skip: !isSupported,
    fetchPolicy: 'cache-and-network',
  });
  const publicKey = data?.pushPublicKey ?? null;

  const [saveSubscription] = useMutation<
    SavePushSubscriptionMutation,
    SavePushSubscriptionMutationVariables
  >(SAVE_PUSH_SUBSCRIPTION);
  const [deleteSubscription] = useMutation<
    DeletePushSubscriptionMutation,
    DeletePushSubscriptionMutationVariables
  >(DELETE_PUSH_SUBSCRIPTION);

  // Reflect the current device's subscription state on mount.
  useEffect(() => {
    if (!isSupported) return undefined;
    let cancelled = false;
    serviceWorkerReady()
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => {
        if (!cancelled) setIsSubscribed(!!sub);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    setError(null);
    if (!isSupported) {
      setError('This browser does not support push notifications.');
      return;
    }
    if (!publicKey) {
      setError('Push notifications are not configured on the server.');
      return;
    }
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        setError('Notification permission was not granted.');
        return;
      }

      const registration = await serviceWorkerReady();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // Cast: the DOM lib types applicationServerKey as BufferSource, and the
        // generic Uint8Array<ArrayBufferLike> isn't structurally accepted.
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      const json = subscription.toJSON();
      try {
        await saveSubscription({
          variables: {
            input: {
              endpoint: subscription.endpoint,
              keys: {
                p256dh: json.keys?.p256dh ?? '',
                auth: json.keys?.auth ?? '',
              },
              userAgent: navigator.userAgent,
            },
          },
        });
        setIsSubscribed(true);
      } catch (saveError) {
        // Roll back the browser subscription so local and server state don't
        // diverge (the mount reconciler keys `isSubscribed` off the browser
        // subscription, which would otherwise show "on" with no server record).
        await subscription.unsubscribe().catch(() => undefined);
        throw saveError;
      }
    } catch (err) {
      log.error('Failed to enable push notifications:', err);
      setError('Could not enable push notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [isSupported, publicKey, saveSubscription]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    if (!isSupported) return;
    setBusy(true);
    try {
      const registration = await serviceWorkerReady();
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const { endpoint } = subscription;
        // Delete the server record first — the local unsubscribe() rarely fails,
        // so this minimises the window where the server still targets a dead
        // endpoint if the network round-trip fails.
        await deleteSubscription({ variables: { endpoint } });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      log.error('Failed to disable push notifications:', err);
      setError('Could not disable push notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [isSupported, deleteSubscription]);

  return {
    isSupported,
    isConfiguredOnServer: !!publicKey,
    permission,
    isSubscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
  };
}
